// Home for all GOT related functions
import {
  AD_NETWORK_CODE,
  AD_PATH_BASE,
  AD_PATH_L0,
  adSizesMap,
  GPT_COMPONENT_ID,
  GPT_GLOBAL_PROPERTIES,
} from '@/lib/ads/constants/adSlots'
import {
  type AdConfig,
  type AdSizesArray,
  type GotAdSettingsSlot,
  type GptAdSlot,
  type GptTargetingProps,
  type GptValue,
  type MediaTypes,
  type srpGpt,
  isGptSlots,
} from '@/lib/ads/types/adConfig'

import { type BidConfig } from '../constants/prebid'
/**
 * returns a hardcoded sizes array for a given ad unit
 * sizes array is passed to GPT as part of ad slot config
 * @param adUnitId same as AdvertisingSlot div id
 * @returns AdSizesArray, e.g. [[300, 250], [300, 600]]
 */
export const getSizesForAdUnit = (adUnitId: string): AdSizesArray | undefined => {
  return adSizesMap[adUnitId]
}
/**
 * add the sizes array to the mediaTypes object
 * mediaTypes object is passed to Prebid as part of ad slot config
 * @param sizes array sizes for a given ad unit
 * @returns MediaTypes object, e.g. { banner: { sizes: [[300, 250], [300, 600]] } }
 */
export const createMediaTypes = (sizes: AdSizesArray): MediaTypes => {
  return {
    banner: {
      sizes,
    },
  }
}
/**
 * returns the SRP page type based on totalListings and keywords
 * pageType is used to determine the GOT endpoint
 * @param totalListings
 * @param keywords
 */
export const getSearchPageType = (totalListings: number, keywords: string) => {
  if (totalListings === 0) return 'zsrp'
  if (keywords) return 'srp'

  return 'brp'
}

/**
 * return values based on naming of GOT endpoints:
 *  - SRPS = search results page (keyword search)
 *  - SRPB = browse results page (no keyword)
 *  - ZSRP = zero search results page
 *  @param totalListings
 *  @param keywords
 */
export const generateGotEndpoint = (
  totalListings: number,
  keywords: string,
  categoryId = '',
  isMobileForAds = false
) => {
  // currently no tablet endpoint in GOT
  const deviceEndpoint = isMobileForAds ? 'MWEB' : 'DESKTOP'

  const pageType = getSearchPageType(totalListings, keywords)

  if (pageType === 'zsrp') {
    return `${deviceEndpoint}/ZSRP/${categoryId}`
  }

  if (pageType === 'srp') {
    return `${deviceEndpoint}/SRPS/${categoryId}`
  }

  return `${deviceEndpoint}/SRPB/${categoryId}`
}

/**
 * If the GOT response fails, we can use the endpoint
 * as key for getting the hardcoded fallbacks
 * @param gotEndpoint
 */
export const getFallbackKey = (gotEndpoint: string) => {
  /**
   * should match both:
   * {device}/{pageType}/{categoryId} eg MWEB/SRPS/123
   * {device}/{pageType} eg MWEB/SRPS
   * */
  const regex = /^\/{0,1}(\w+)\/(\w+)\/*\d*\/{0,1}$/

  // convert to key format, eg MWEB_SRPS
  const fallbackKey = gotEndpoint.replace(regex, '$1_$2')

  return fallbackKey
}

/**
 * lazyLoad is enabled globally in gptGlobalProperties,
 * but we want to disable it for top leaderboard & skyscraper,
 * which are already in the viewport on first load.
 *
 * @param id id for ad
 * @returns boolean
 */
export const shouldDisableLazyLoad = (id: string) => {
  return (
    id === GPT_COMPONENT_ID.TOP_LEADERBOARD ||
    id === GPT_COMPONENT_ID.SKYSCRAPER ||
    id === GPT_COMPONENT_ID.MWEB_LEADERBOARD
  )
}

export const flattenGptArray = (gpt: srpGpt[]) => {
  const flattenedArray: { [key: string]: GptValue } = {}

  gpt.forEach((entry) => {
    flattenedArray[entry.key] = entry.value
  })

  return flattenedArray
}

/**
 * Adds a labelAny array with the sizeMappingName to each bidder
 * This lets us refernce the correct sizeConfig in the global Prebid config
 * sizeConfig is the Prebid equivalent of the GPT sizeMapping
 * @param id ad unit id
 * @param bids Prebid bidder params
 * @returns bids with labelAny added to each bidder
 */
export const processPrebidBids = (id: string, bids: BidConfig[]) => {
  const gptComponentIdKeys = Object.keys(GPT_COMPONENT_ID)

  const bidsWithLabelAny = bids.map((bid) => {
    const sizeMappingName = gptComponentIdKeys.find((key) => id.startsWith(GPT_COMPONENT_ID[key]))

    return {
      ...bid,
      ...(sizeMappingName && { labelAny: [sizeMappingName] }),
    }
  })

  return bidsWithLabelAny
}

/**
 * @param pathLevel path level to be sanitized
 * @returns a sanitized path level, eg: input: women's -> output: womens
 */
const charsToBeReplacedFromPath = /[!"#$%&'()*+,./:;<=>?@[\]^_`{|}~'"'“”‘’•…—–\s]/g // All special chars and a whitespace excluding dash -
const pathCharsReplacement = '_'
const sanitizePathLevel = (pathLevel: string | string[]) => {
  if (Array.isArray(pathLevel)) {
    if (!pathLevel.some((pathLevel) => charsToBeReplacedFromPath.test(pathLevel))) return pathLevel
    return pathLevel.map((pathLevel) =>
      pathLevel.replace(charsToBeReplacedFromPath, pathCharsReplacement)
    )
  }
  return charsToBeReplacedFromPath.test(pathLevel)
    ? pathLevel.replace(charsToBeReplacedFromPath, pathCharsReplacement)
    : pathLevel
}

/**
 * @param gptTargetingProps targeting values that get passed to GPT
 * @returns ad unit path, eg '/5138/kij.ca.srp-web-en/buy___sell/audio/ipod___mp3_accessories'
 */
export const generateAdUnitPath = (gptTargetingProps: GptTargetingProps) => {
  const { lang, pagetype, l1, l2, l3 } = gptTargetingProps

  const AD_PATH_L1 = l1 ? l1 : AD_PATH_L0 // L1 undefined if in All Categories
  const AD_PATH_L2 = l2 ? `/${sanitizePathLevel(l2)}` : ''
  const AD_PATH_L3 = l3 ? `/${sanitizePathLevel(l3)}` : ''

  return `/${AD_NETWORK_CODE}/${AD_PATH_BASE}${pagetype}-web-${lang}/${AD_PATH_L1}${AD_PATH_L2}${AD_PATH_L3}`
}

/**
 *
 * @param adSettings adSettings from GOT API response
 * @param gptTargetingProps page-level targeting values
 * @returns
 */
export const processGotAdSettings = (
  adSettings: GotAdSettingsSlot[],
  gptTargetingProps: GptTargetingProps
) => {
  const gptAdSlots = adSettings.reduce((acc: GptAdSlot[], curr) => {
    if (!isGptSlots(curr.settings)) return acc

    const { id, collapseEmptyDiv, bids, sizeMappingName, pos } = curr.settings

    // we are now overriding all the GOT sizes with the hardcoded sizes
    const isInlineAd = id.startsWith(GPT_COMPONENT_ID.INLINE)
    const sizes = isInlineAd ? adSizesMap[GPT_COMPONENT_ID.INLINE] : adSizesMap[id]

    const mediaTypes = createMediaTypes(sizes)

    const bidderParams = bids ? processPrebidBids(id, bids) : []

    const gptAdSlot: GptAdSlot = {
      id,
      collapseEmptyDiv,
      sizeMappingName,
      sizes,
      targeting: id === GPT_COMPONENT_ID.TOP_LEADERBOARD ? { pos: 'top_header' } : { pos }, // disable pos for top leaderboard
      metaData: {}, // prevent issue with React Advertising
      prebid: [{ mediaTypes, bids: bidderParams }],
      ...(shouldDisableLazyLoad(id) && { enableLazyLoad: false }),
    }

    return [...acc, gptAdSlot]
  }, [])

  /**
   * merge transformed GOT config with the hardcoded
   * global GPT properties.
   * Also merging in hardcoded MINI_LEADERBOARD_GPT for testing sizes
   */
  const adConfig: AdConfig = {
    ...GPT_GLOBAL_PROPERTIES,
    path: generateAdUnitPath(gptTargetingProps),
    targeting: gptTargetingProps,
    slots: gptAdSlots,
  }

  return adConfig
}
